import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import type { ScheduledReport } from "api/models";
import { useGetScheduledReportOptions } from "api/queries/user/scheduledReports/useGetScheduledReportOptions";
import { createScheduledReport, updateScheduledReport } from "api/services/user";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Button from "components/v2/Button/Button";
import Modal from "components/v2/Modal/Modal";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import ScheduledReportFormFields from "./ScheduledReportFormFields";
import { buildScheduledReportPayload, getDefaultFormValues, mapReportToFormValues } from "./scheduledReportHelpers";
import { ScheduledReportFormValues } from "./scheduledReportTypes";

const FORM_ID = "scheduled-report-form";

type ScheduledReportDialogProps = {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  report?: ScheduledReport | null;
};

function ScheduledReportDialog({ show, onHide, onSuccess, report }: ScheduledReportDialogProps) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isProvider = user_type === "provider";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm<ScheduledReportFormValues>({
    // onTouched, not onChange: don't flag a half-typed email on every keystroke.
    mode: "onTouched",
    defaultValues: getDefaultFormValues(isProvider),
  });
  const { handleSubmit, reset } = methods;
  const { data: options, isLoading, isError } = useGetScheduledReportOptions(show);

  useEffect(() => {
    if (!show || !options) return;
    if (report) {
      reset(mapReportToFormValues(report, options));
      return;
    }
    reset(getDefaultFormValues(isProvider, options));
  }, [show, report, options, isProvider, reset]);

  const onSubmit = (formData: ScheduledReportFormValues) => {
    const payload = buildScheduledReportPayload(formData, options, isProvider);
    setIsSubmitting(true);
    const request = report ? updateScheduledReport(report.id, payload) : createScheduledReport(payload);
    request
      .then(() => {
        toast(
          intl.formatMessage({
            id: report ? "report.scheduledUpdated" : "report.scheduledCreated",
            defaultMessage: report ? "Scheduled report updated." : "Report generation scheduled successfully.",
          }),
          { type: "success" },
        );
        onSuccess();
        onHide();
      })
      .catch((error) => errorToast(error))
      .finally(() => setIsSubmitting(false));
  };

  const showSkeleton = show && isLoading;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header>
        <div className="d-flex flex-column">
          <div className="text-primary text-xl font-semibold">
            {intl.formatMessage({
              id: report ? "report.editScheduledReport" : "report.scheduleReport",
              defaultMessage: report ? "Edit scheduled report" : "Schedule report",
            })}
          </div>
          <div className="mt-xs text-tertiary text-sm">
            <FormattedMessage
              id="report.scheduleReportIntro"
              defaultMessage="Change the ranges to view the data you are looking for or schedule a report to be e-mailed at a given interval."
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="scheduled-report-modal__body">
        <div className="mt-2xl">
          {showSkeleton && <LoadingSkeleton rows={3} className="p-0" isListing />}
          {!showSkeleton && isError && (
            <p className="mb-0 text-danger">
              <FormattedMessage id="general.couldNotLoadDetails" defaultMessage="We couldn't load these details." />
            </p>
          )}
          {!showSkeleton && !isError && (
            <FormProvider {...methods}>
              <Form id={FORM_ID} className="w-100" onSubmit={handleSubmit(onSubmit)}>
                {options && (
                  <ScheduledReportFormFields
                    key={report ? report.id : "new"}
                    options={options}
                    isProvider={isProvider}
                    isEdit={Boolean(report)}
                  />
                )}
              </Form>
            </FormProvider>
          )}
        </div>
      </Modal.Body>
      {!showSkeleton && !isError && (
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end gap-lg">
            <Button
              variant="secondary-gray"
              label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
              onClick={onHide}
            />
            <Button
              type="submit"
              form={FORM_ID}
              disabled={isSubmitting}
              testId={testIds.ANALYTICS_SCHEDULED_REPORT_SUBMIT_BUTTON}
              label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
            />
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default ScheduledReportDialog;
